import { memo, useCallback, useMemo } from 'react';

import * as ModalWindows from '@Stores/ModalWindows';
import * as PageProductStore from '@Stores/PageProduct';
import useTranslation from '@Queries/useTranslation';
import Configurator from '../Configurator';

import type { MouseEvent } from 'react';
import type { ConfiguratorProps } from '../Configurator';
import type {
  ConfiguratorTab,
  ReadySolutionsData,
  ConfiguratorOptionItemProduct,
  ConfiguratorOption,
} from '@Pages/PageProduct/typings';
import type { Parameter } from '@Pages/PageProduct';
import type { ChangeConfigurationProps } from '../../Sidebar';
import type { ModuleProductData } from '@Types/ModuleProduct';

export interface FakeConfiguratorProps extends ConfiguratorProps {
  className?: string;
  parameters?: Parameter[];
  readySolutions?: ReadySolutionsData;
  modules?: ModuleProductData[];
  readySolutionsIsFirst?: boolean;
}

const getOptions = (parameters: Parameter[], needOptionTitle?: boolean): ConfiguratorOption[] => {
  return parameters.map((parameter) => {
    const theme = parameter.variants[0].theme;
    // TODO: придумать что-то получше с единицами измерения
    const reserveUnit = parameter.groupId === 3 || parameter.groupId === 2 ? 'см' : null;

    return {
      title: needOptionTitle && parameter.name,
      theme: theme === 'square' ? 'image' : 'tabs',
      items: parameter.variants?.map((variant) => {
        return {
          type: 'parameter',
          title: variant.name,
          disabled: false,
          id: 0,
          link: variant.url,
          ...variant,
          price: { actual: variant.price },
          minCount: 0,
          maxCount: 0,
          count: 0,
          unit: variant.unit?.name || reserveUnit,
        };
      }),
      parameterId: parameter.groupId,
      id: parameter.value,
    };
  });
};

const FakeConfigurator = ({
  configurator,
  parameters,
  readySolutions,
  modules,
  readySolutionsIsFirst,
  ...restProps
}: FakeConfiguratorProps) => {
  const { t } = useTranslation();

  const handleOpenModulesModal = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      ModalWindows.open('ModulesSet', {
        modules,
      });
    },
    [modules],
  );

  const tabs = useMemo<ConfiguratorTab[]>(() => {
    const result: ConfiguratorTab[] = [];

    if (parameters?.length > 0) {
      const legs = parameters.find((parameter) => parameter.groupId === 97);
      const sleepPlace = parameters.find((parameter) => parameter.groupId === 3);
      const sizes = parameters.find((parameter) => parameter.groupId === 2);

      if (legs) {
        result.push({
          id: 'legs',
          count: legs.variants.length,
          title: legs.name,
          options: getOptions([legs]),
        });
      }

      if (sleepPlace) {
        result.push({
          id: 'size',
          count: sleepPlace.variants.length,
          title: t('sizes'),
          options: getOptions([sleepPlace], true),
        });
      }

      if (sizes) {
        result.push({
          id: 'size',
          count: sizes.variants.length,
          title: t('sizes'),
          options: getOptions([sizes]),
        });
      }

      const otherParameters = parameters.filter((param) => ![97, 3, 2].includes(param.groupId));
      if (otherParameters.length > 0) {
        result.push({
          id: 'parameters',
          count: null,
          title: t('options.title'),
          options: getOptions(otherParameters, true),
        });
      }
    }

    if (modules?.length > 0) {
      result.push({
        id: 'modules',
        count: modules.length,
        selectedCount: modules.reduce(
          (acc, module) => (module.count ? acc + module.count : acc),
          0,
        ),
        title: t('modules-tab-name'),
        options: [],
        view: 'modal',
        onClick: handleOpenModulesModal,
      });
    }

    if (readySolutions?.colors?.length > 0) {
      const colors = readySolutions.colors || [];

      const items: ConfiguratorOptionItemProduct[] = colors.map(
        ({ shopProductId, link, image, price, materials, name }) => ({
          type: 'product',
          id: shopProductId,
          title: name,
          disabled: false,
          selected: shopProductId === configurator.configuration.id,
          link,
          image,
          price,
          minCount: 0,
          maxCount: 0,
          count: 0,
          materials,
        }),
      );

      const actual = colors.reduce(
        (prev, { price }) => Math.min(prev, price?.actual || Number.MAX_VALUE),
        Number.MAX_VALUE,
      );
      const tab: ConfiguratorTab = {
        id: 'upholstery',
        canOrderSamples: readySolutions.canOrderSamples,
        count: colors.length,
        title: readySolutions.tabName,
        options: [
          {
            ...readySolutions.collection,
            title: readySolutions.collection?.title,
            theme: 'color',
            minPrice: { actual },
            items,
            parameterId: colors[0]?.materials[0]?.parameterId,
            id: readySolutions.collection?.id.toString(),
          },
        ],
      };

      if (readySolutionsIsFirst) {
        result.unshift(tab);
      } else {
        result.push(tab);
      }
    }
    return result;
  }, [
    parameters,
    modules,
    readySolutions,
    t,
    handleOpenModulesModal,
    readySolutionsIsFirst,
    configurator.configuration.id,
  ]);

  const handleChangeParameter = useCallback((e: MouseEvent, params: ChangeConfigurationProps) => {
    const { item, parameterId } = params.item;
    PageProductStore.selectParameter({ groupId: parameterId, variantId: item.id });
  }, []);

  return (
    <Configurator
      {...restProps}
      configurator={{ ...configurator, tabs }}
      onClickItem={handleChangeParameter}
    />
  );
};

export default memo(FakeConfigurator);
